/**
 * Formulaire de réinitialisation de mot de passe
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Formulaire multi-étapes pour réinitialiser le mot de passe.
 */

 // Client Component pour Next.js

import { useState } from 'react';
import { usePasswordStrength } from '../../hooks';
import { PasswordStrengthMeter } from '../ui/PasswordStrengthMeter';
import { ButtonSpinner } from '../ui/LoadingSpinner';
import { securityApi } from '../../../infrastructure/api';

/**
 * Props du composant ResetPasswordForm
 */
export interface ResetPasswordFormProps {
  /** Token de réinitialisation depuis l'URL (optionnel) */
  token?: string;
  /** Callback après réinitialisation réussie */
  onSuccess?: () => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Callback pour retour à la connexion */
  onBackToLogin?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Étapes du formulaire
 */
type Step = 'email' | 'password';

/**
 * Composant ResetPasswordForm
 *
 * @example
 * ```tsx
 * function ResetPasswordPage() {
 *   const router = useNavigate();
 *
 *   return (
 *     <ResetPasswordForm
 *       onSuccess={() => navigate('/login')}
 *       onBackToLogin={() => navigate('/login')}
 *     />
 *   );
 * }
 * ```
 */
export function ResetPasswordForm({
  token,
  onSuccess,
  onError,
  onBackToLogin,
  className = '',
}: ResetPasswordFormProps) {
  // Si un token est fourni, démarrer directement à l'étape password
  const [step, setStep] = useState<Step>(token ? 'password' : 'email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    nouveauMotDePasse: '',
    confirmationMotDePasse: '',
  });
  // Si un token est fourni, l'utiliser directement
  const [resetToken, setResetToken] = useState<string | null>(token || null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const passwordStrength = usePasswordStrength(formData.nouveauMotDePasse);

  /**
   * Étape 1 : Demander le reset
   */
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFormErrors({});

    if (!formData.email) {
      setFormErrors({ email: 'L\'email est requis' });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setFormErrors({ email: 'Format d\'email invalide' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await securityApi.requestPasswordReset({
        email: formData.email,
      });

      if (!response.succes) {
        throw new Error(response.message || 'Erreur lors de la demande');
      }

      // Email envoyé avec succès - informer l'utilisateur de vérifier ses emails
      alert('Un email avec un lien de réinitialisation vous a été envoyé. Veuillez vérifier votre boîte de réception.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de demande de réinitialisation';
      setError(message);
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Étape 2 : Définir le nouveau mot de passe
   */
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFormErrors({});

    const errors: Record<string, string> = {};

    if (!formData.nouveauMotDePasse) {
      errors.nouveauMotDePasse = 'Le mot de passe est requis';
    } else if (passwordStrength.score < 3) {
      errors.nouveauMotDePasse = 'Le mot de passe n\'est pas assez fort';
    }

    if (!formData.confirmationMotDePasse) {
      errors.confirmationMotDePasse = 'La confirmation est requise';
    } else if (formData.nouveauMotDePasse !== formData.confirmationMotDePasse) {
      errors.confirmationMotDePasse = 'Les mots de passe ne correspondent pas';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await securityApi.resetPassword({
        token: resetToken!,
        nouveauMotDePasse: formData.nouveauMotDePasse,
        confirmationMotDePasse: formData.confirmationMotDePasse,
      });

      if (!response.succes) {
        throw new Error(response.message || 'Erreur lors de la réinitialisation');
      }

      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de réinitialisation';
      setError(message);
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Gérer le changement de champ
   */
  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className={`w-full max-w-md ${className}`}>
      {/* Indicateur d'étapes */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {(['email', 'password'] as Step[]).map((s, index) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step === s
                    ? 'bg-primary text-content-inverted'
                    : index < ['email', 'password'].indexOf(step)
                    ? 'bg-success text-content-inverted'
                    : 'bg-surface-overlay text-content-muted'
                }`}
              >
                {index < ['email', 'password'].indexOf(step) ? '✓' : index + 1}
              </div>
              {index < 1 && (
                <div
                  className={`w-16 h-1 ${
                    index < ['email', 'password'].indexOf(step)
                      ? 'bg-success'
                      : 'bg-surface-overlay'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Erreur globale */}
      {error && (
        <div
          className="mb-6 p-4 bg-danger-light border border-danger rounded-lg"
          role="alert"
        >
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Étape 1 : Email */}
      {step === 'email' && (
        <form onSubmit={handleRequestReset} className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-content">
              Réinitialiser le mot de passe
            </h2>
            <p className="mt-2 text-sm text-content-muted">
              Entrez votre adresse email pour recevoir un code de réinitialisation
            </p>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-content-muted mb-1">
              Adresse email
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              disabled={isLoading}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                formErrors.email ? 'border-danger' : 'border-border'
              } bg-surface text-content`}
              placeholder="votre@email.com"
              autoComplete="email"
            />
            {formErrors.email && <p className="mt-1 text-sm text-danger">{formErrors.email}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <ButtonSpinner className="mr-2" />
                Envoi...
              </>
            ) : (
              'Envoyer le code'
            )}
          </button>

          {onBackToLogin && (
            <div className="text-center">
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm font-medium text-primary hover:text-primary-hover"
              >
                Retour à la connexion
              </button>
            </div>
          )}
        </form>
      )}

      {/* Étape 2 : Nouveau mot de passe */}
      {step === 'password' && (
        <form onSubmit={handleResetPassword} className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-content">
              Nouveau mot de passe
            </h2>
            <p className="mt-2 text-sm text-content-muted">
              Choisissez un mot de passe fort pour votre compte
            </p>
          </div>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-content-muted mb-1">
              Nouveau mot de passe
            </label>
            <input
              id="new-password"
              type="password"
              value={formData.nouveauMotDePasse}
              onChange={handleChange('nouveauMotDePasse')}
              disabled={isLoading}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                formErrors.nouveauMotDePasse ? 'border-danger' : 'border-border'
              } bg-surface text-content`}
              autoComplete="new-password"
            />
            {formErrors.nouveauMotDePasse && (
              <p className="mt-1 text-sm text-danger">{formErrors.nouveauMotDePasse}</p>
            )}
            {formData.nouveauMotDePasse && (
              <div className="mt-3">
                <PasswordStrengthMeter strength={passwordStrength} />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-content-muted mb-1">
              Confirmer le mot de passe
            </label>
            <input
              id="confirm-password"
              type="password"
              value={formData.confirmationMotDePasse}
              onChange={handleChange('confirmationMotDePasse')}
              disabled={isLoading}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                formErrors.confirmationMotDePasse ? 'border-danger' : 'border-border'
              } bg-surface text-content`}
              autoComplete="new-password"
            />
            {formErrors.confirmationMotDePasse && (
              <p className="mt-1 text-sm text-danger">{formErrors.confirmationMotDePasse}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || passwordStrength.score < 3}
            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <ButtonSpinner className="mr-2" />
                Réinitialisation...
              </>
            ) : (
              'Réinitialiser le mot de passe'
            )}
          </button>
        </form>
      )}
    </div>
  );
}
